import React, { useState, useEffect, useCallback } from "react";
import { RouteComponentProps } from "@reach/router";
import "./StyleGuide.scss";
import { useCompanyInfo } from "../redux/company";
import { useSetError } from "../redux/modals";
import { BPMButton, Input, Dropdown } from "../Components";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { Modal } from "react-bootstrap";
import { MdSearch, MdEdit, MdDelete, MdContentCopy, MdSave, MdAdd, MdUndo } from "react-icons/md";
import { S3SignedUrlFetch2 } from "../utils/fetch-utils";
import {
  getCopytigerStyleGuides,
  addCopytigerStyleGuide,
  deleteCopytigerStyleGuide,
  StyleGuideOption,
} from "./copytigerUtils";

const HOW_TO_VIDEO = {
  SUBTITLE_TEXT: "Read on how to create a good style guide",
  HELP_URL: "https://www.google.com/search?q=how+to+create+a+style+guide+for+llm",
  HELP_LINK_TEXT: "here",
} as const;

const StyleGuide = (props: RouteComponentProps): JSX.Element => {
  const [selectedGuide, setSelectedGuide] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("a-to-z");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableTitle, setEditableTitle] = useState<string>("");
  const [editableDescription, setEditableDescription] = useState<string>("");
  const [editableContent, setEditableContent] = useState<string>("");
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingGuideSelection, setPendingGuideSelection] = useState<string | null>(null);
  const [styleGuides, setStyleGuides] = useState<StyleGuideOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentContent, setCurrentContent] = useState<string>("");
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);
  const [dialog, setDialog] = useState<{
    show: boolean;
    title: string;
    body: string;
    variant: AlertDialogVariants;
    onConfirm?: () => void;
  } | null>(null);

  const companyInfo = useCompanyInfo();
  const companyID = companyInfo.cid;
  const setError = useSetError();

  const fetchStyleGuides = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getCopytigerStyleGuides(companyID);
      setStyleGuides(data);

      if (data.length > 0 && !selectedGuide) {
        setSelectedGuide(data[0].name);
      }
    } catch (error) {
      setStyleGuides([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyID, selectedGuide]);

  useEffect(() => {
    if (companyID) {
      fetchStyleGuides();
    }
  }, [companyID, fetchStyleGuides]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isEditMode) {
        exitEditMode();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isEditMode]);

  const selectedGuideData = styleGuides.find(guide => guide.name === selectedGuide);

  const formatContent = (content: string): string => {
    try {
      const parsed = JSON.parse(content);
      return parsed.content || content;
    } catch (e) {
      return content;
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Unknown";
      }
      return date.toLocaleDateString();
    } catch (e) {
      return "Unknown";
    }
  };

  const fetchS3Content = async (s3Uri: string): Promise<string> => {
    const s3Key = s3Uri.replace("s3://bpm-cdn/", "");

    const response = await S3SignedUrlFetch2("bpm-cdn", s3Key, "application/json");

    if (!response.ok) {
      throw new Error(`Failed to fetch S3 content: ${response.statusText}`);
    }

    return await response.text();
  };

  useEffect(() => {
    const loadContent = async () => {
      if (!selectedGuideData?.s3uri) {
        setCurrentContent("");
        return;
      }

      setIsLoadingContent(true);
      try {
        const content = await fetchS3Content(selectedGuideData.s3uri);
        setCurrentContent(content);
      } catch (error) {
        setCurrentContent("Error loading style guide content.");
      } finally {
        setIsLoadingContent(false);
      }
    };

    loadContent();
  }, [selectedGuide, styleGuides, selectedGuideData?.s3uri]);

  const enterEditMode = () => {
    if (!selectedGuideData) {
      return;
    }
    setEditableTitle(selectedGuideData.name);
    setEditableDescription(selectedGuideData.description || "");
    setEditableContent(formatContent(currentContent));
    setIsEditMode(true);
  };

  const handleSaveAll = async () => {
    if (!selectedGuideData) {
      setIsEditMode(false);
      return;
    }

    const trimmedTitle = editableTitle.trim();
    const trimmedDescription = editableDescription.trim();
    const trimmedContent = editableContent.trim();

    if (!trimmedTitle) {
      setError({ message: "Style guide name cannot be empty." });
      return;
    }

    if (!trimmedContent) {
      setError({ message: "Content cannot be empty." });
      return;
    }

    if (trimmedDescription.length > 250) {
      setError({ message: "Description must be 250 characters or less." });
      return;
    }

    if (
      trimmedTitle === selectedGuideData.name &&
      trimmedDescription === (selectedGuideData.description || "") &&
      trimmedContent === formatContent(currentContent)
    ) {
      setIsEditMode(false);
      if (pendingGuideSelection) {
        setSelectedGuide(pendingGuideSelection);
        setPendingGuideSelection(null);
      }
      setShowUnsavedChangesModal(false);
      return;
    }

    try {
      const contentChanged = trimmedContent !== formatContent(currentContent);
      const nameChanged = trimmedTitle !== selectedGuideData.name;
      const descriptionChanged = trimmedDescription !== (selectedGuideData.description || "");

      if (contentChanged) {
        const contentToSave = JSON.stringify({ content: trimmedContent });
        await addCopytigerStyleGuide(
          companyID,
          trimmedTitle,
          contentToSave,
          trimmedDescription,
          selectedGuideData.id
        );
      } else if (nameChanged || descriptionChanged) {
        await addCopytigerStyleGuide(
          companyID,
          trimmedTitle,
          "",
          trimmedDescription,
          selectedGuideData.id
        );
      }

      await fetchStyleGuides();
      setSelectedGuide(trimmedTitle);
      setIsEditMode(false);
      if (pendingGuideSelection) {
        setSelectedGuide(pendingGuideSelection);
        setPendingGuideSelection(null);
      }
      setShowUnsavedChangesModal(false);
    } catch (error) {
      setError({
        message: "Failed to update style guide. Please check your network connection.",
      });
    }
  };

  const exitEditMode = () => {
    setIsEditMode(false);
    setEditableTitle("");
    setEditableDescription("");
    setEditableContent("");
  };

  const hasUnsavedChanges = () => {
    if (!selectedGuideData || !isEditMode) {
      return false;
    }

    return (
      editableTitle.trim() !== selectedGuideData.name ||
      editableDescription.trim() !== (selectedGuideData.description || "") ||
      editableContent.trim() !== formatContent(currentContent)
    );
  };

  const handleGuideSelection = (guideName: string) => {
    if (isEditMode && hasUnsavedChanges()) {
      setPendingGuideSelection(guideName);
      setShowUnsavedChangesModal(true);
    } else {
      setSelectedGuide(guideName);
      if (isEditMode) {
        exitEditMode();
      }
    }
  };

  const confirmGuideSwitch = () => {
    if (pendingGuideSelection) {
      setSelectedGuide(pendingGuideSelection);
      setPendingGuideSelection(null);
    }
    exitEditMode();
    setShowUnsavedChangesModal(false);
  };

  const cancelGuideSwitch = () => {
    setPendingGuideSelection(null);
    setShowUnsavedChangesModal(false);
  };

  const handleCopyContent = (guide: StyleGuideOption) => {
    if (!currentContent || selectedGuide !== guide.name) {
      setError({ message: "Please select the style guide to copy its content." });
      return;
    }

    const contentToCopy = formatContent(currentContent);

    navigator.clipboard.writeText(contentToCopy).then(() => {
      setDialog({
        show: true,
        title: "Content Copied",
        body: "Style guide content has been copied to your clipboard!",
        variant: AlertDialogVariants.SUCCESS,
      });
    });
  };

  const handleDelete = () => {
    if (!selectedGuideData) {
      return;
    }

    setDialog({
      show: true,
      title: "Confirm Deletion",
      body: `Are you sure you want to delete "${selectedGuideData.name}"? This action cannot be undone.`,
      variant: AlertDialogVariants.WARNING,
      onConfirm: async () => {
        try {
          await deleteCopytigerStyleGuide(companyID, selectedGuideData.id);

          setCurrentContent("");
          setSelectedGuide("");

          if (isEditMode) {
            setIsEditMode(false);
            setEditableTitle("");
            setEditableDescription("");
            setEditableContent("");
          }

          const updatedGuides = await getCopytigerStyleGuides(companyID);
          setStyleGuides(updatedGuides);

          if (updatedGuides.length > 0) {
            setTimeout(() => {
              setSelectedGuide(updatedGuides[0].name);
            }, 0);
          }

          setDialog({
            show: true,
            title: "Style Guide Deleted",
            body: `"${selectedGuideData.name}" has been successfully deleted.`,
            variant: AlertDialogVariants.SUCCESS,
          });
        } catch (error) {
          setError({
            message: "Failed to delete style guide. Please check your network connection.",
          });
        }
      },
    });
  };

  const handleCreateNew = async () => {
    const defaultName = "New Style Guide";
    let uniqueName = defaultName;
    let counter = 1;

    const existingNames = styleGuides.map(guide => guide.name.toLowerCase());
    while (existingNames.includes(uniqueName.toLowerCase())) {
      uniqueName = `${defaultName} ${counter}`;
      counter++;
    }

    const defaultContent = {
      content: `${uniqueName}\n\nThis is a new style guide. Add your content here...\n\nGuidelines:\n- Add your brand voice\n- Include messaging pillars\n- Define target audience`,
    };

    try {
      await addCopytigerStyleGuide(companyID, uniqueName, JSON.stringify(defaultContent), "");
      await fetchStyleGuides();
      setSelectedGuide(uniqueName);

      setEditableTitle(uniqueName);
      setEditableDescription("");
      setEditableContent(defaultContent.content);
      setIsEditMode(true);
    } catch (error) {
      setError({ message: "Failed to create style guide. Please check your network connection." });
    }
  };

  const filteredGuides = styleGuides.filter(guide =>
    guide.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedGuides = [...filteredGuides].sort((a, b) => {
    if (sortBy === "a-to-z") {
      return a.name.localeCompare(b.name);
    }
    return b.name.localeCompare(a.name);
  });

  if (isLoading) {
    return (
      <div className="styleguide-container">
        <div className="bpm-styleguide">
          <div className="styleguide-header">
            <div className="header-text">
              <h1 className="styleguide-title">
                {companyID.charAt(0).toUpperCase() + companyID.slice(1)} Style Guides
              </h1>
              <p className="styleguide-subtitle">Loading style guides...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="styleguide-container">
      <div className="bpm-styleguide">
        <div className="styleguide-header">
          <div className="header-text">
            <h1 className="styleguide-title">
              {companyID.charAt(0).toUpperCase() + companyID.slice(1)} Style Guides
            </h1>
            <p className="styleguide-subtitle">
              {HOW_TO_VIDEO.SUBTITLE_TEXT}{" "}
              <a href={HOW_TO_VIDEO.HELP_URL} target="_blank" rel="noopener noreferrer">
                {HOW_TO_VIDEO.HELP_LINK_TEXT}
              </a>
              .
            </p>
          </div>
          <div className="styleguide-header-actions">
            {selectedGuide && !isEditMode && (
              <BPMButton variant="primary" size="sm" onClick={enterEditMode}>
                <MdEdit /> Edit Style Guide
              </BPMButton>
            )}
            <BPMButton variant="primary" size="sm" onClick={handleCreateNew}>
              <MdAdd /> Add New Style Guide
            </BPMButton>
          </div>
        </div>

        <div className="styleguide-content">
          <div className="styleguide-sidebar">
            <div className="sidebar-search-sort">
              <div className="search-container">
                <MdSearch className="search-icon" />
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <Dropdown
                value={sortBy}
                options={[
                  { value: "a-to-z", label: "A to Z" },
                  { value: "z-to-a", label: "Z to A" },
                ]}
                onChange={value => setSortBy(value)}
                className="sort-dropdown"
                size="sm"
                label="Sort by"
                background="light"
              />
            </div>

            <div className="sidebar-list">
              {sortedGuides.map(guide => (
                <div
                  key={guide.name}
                  className={`styleguide-item ${guide.name === selectedGuide ? "selected" : ""}`}
                  onClick={() => handleGuideSelection(guide.name)}
                >
                  <div className="item-header">
                    <h3 className="item-title">{guide.name}</h3>
                    {guide.name === selectedGuide && (
                      <div className="item-actions">
                        <BPMButton
                          variant="outline-primary"
                          size="sm"
                          className="action-btn edit-btn"
                          title="Edit"
                          onClick={e => {
                            e.stopPropagation();
                            enterEditMode();
                          }}
                        >
                          <MdEdit />
                        </BPMButton>
                        <BPMButton
                          variant="outline-secondary"
                          size="sm"
                          className="action-btn copy-btn"
                          title="Copy Content"
                          onClick={e => {
                            e.stopPropagation();
                            handleCopyContent(guide);
                          }}
                        >
                          <MdContentCopy />
                        </BPMButton>
                        <BPMButton
                          variant="outline-danger"
                          size="sm"
                          className="action-btn delete-btn"
                          title="Delete"
                          onClick={e => {
                            e.stopPropagation();
                            handleDelete();
                          }}
                        >
                          <MdDelete />
                        </BPMButton>
                      </div>
                    )}
                  </div>
                  <p className="item-meta">
                    Last modified {formatDate(guide.lastmodified)} by {guide.lastuser}.
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="styleguide-main">
            {selectedGuideData ? (
              <>
                <div className="main-header">
                  {isEditMode ? (
                    <div className="title-edit-container">
                      <input
                        type="text"
                        value={editableTitle}
                        onChange={e => setEditableTitle(e.target.value)}
                        className="title-input"
                        placeholder="Style guide name..."
                      />
                    </div>
                  ) : (
                    <div className="main-title-display">
                      <h2 className="main-title">{selectedGuideData.name}</h2>
                    </div>
                  )}
                  <div className="main-meta">
                    <span className="meta-info">
                      Last Modified {formatDate(selectedGuideData.lastmodified)} by{" "}
                      {selectedGuideData.lastuser}
                    </span>
                  </div>

                  <div className="description-section">
                    {isEditMode ? (
                      <div className="description-edit-container">
                        <label className="description-label">
                          Description (optional, max 250 chars):
                        </label>
                        <textarea
                          value={editableDescription}
                          onChange={e => setEditableDescription(e.target.value)}
                          className="description-textarea"
                          placeholder="Add a brief description of this style guide..."
                          maxLength={250}
                          rows={3}
                        />
                        <div className="description-char-count">
                          {editableDescription.length}/250 characters
                        </div>
                      </div>
                    ) : (
                      <div className="description-display">
                        <div className="description-content">
                          {selectedGuideData.description ? (
                            <p className="description-text">{selectedGuideData.description}</p>
                          ) : (
                            <p className="description-placeholder">No description</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`main-content ${isEditMode ? "editing-content" : ""}`}>
                  <div className="content-section">
                    {isLoadingContent ? (
                      <div className="section-content">
                        <p>Loading style guide content...</p>
                      </div>
                    ) : isEditMode ? (
                      <div className="content-edit-container">
                        <div className="content-edit-header">
                          <label className="content-label">Style Guide Content:</label>
                        </div>
                        <textarea
                          value={editableContent}
                          onChange={e => setEditableContent(e.target.value)}
                          className="content-textarea"
                          placeholder="Add your style guide content here..."
                          rows={20}
                        />
                      </div>
                    ) : (
                      <div className="content-display-wrapper">
                        <div className="content-display-content">
                          {currentContent ? (
                            <div className="content-text">{formatContent(currentContent)}</div>
                          ) : (
                            <div className="content-placeholder">No content available</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="main-content">
                <p>Select a style guide to view its content</p>
              </div>
            )}

            <div className="main-footer">
              {isEditMode && (
                <>
                  <BPMButton
                    variant="outline-secondary"
                    size="sm"
                    onClick={exitEditMode}
                    className="discard-btn"
                  >
                    <MdUndo /> Discard Edits
                  </BPMButton>
                  <BPMButton variant="primary" size="sm" onClick={handleSaveAll}>
                    <MdSave /> Save Changes
                  </BPMButton>
                </>
              )}
              <BPMButton variant="outline-danger" size="sm" onClick={handleDelete}>
                <MdDelete /> Delete Style Guide
              </BPMButton>
            </div>
          </div>
        </div>
      </div>

      {dialog && (
        <AlertDialog
          show={dialog.show}
          onHide={() => setDialog(null)}
          icon={null}
          title={dialog.title}
          body={dialog.body}
          variant={dialog.variant}
          primaryAction={dialog.onConfirm}
          primaryButtonLabel={dialog.onConfirm ? "Delete" : undefined}
        />
      )}

      <Modal show={showUnsavedChangesModal} onHide={cancelGuideSwitch} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You have unsaved changes to the current style guide. Do you want to discard these
            changes and switch to another style guide?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <BPMButton variant="outline-secondary" size="sm" onClick={cancelGuideSwitch}>
            Cancel
          </BPMButton>
          <BPMButton variant="primary" size="sm" onClick={handleSaveAll}>
            Save Changes
          </BPMButton>
          <BPMButton variant="outline-danger" size="sm" onClick={confirmGuideSwitch}>
            Discard Changes
          </BPMButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StyleGuide;
